import React, { useState, useMemo, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

/**
 * Enhanced PaperSelector component
 * Two-step selection process with side-by-side dropdowns for common papers
 * Papers in modal organized by name first, then weight
 */
const PaperSelector = ({
  paperStocks,
  selectedPaperId,
  onChange,
  className,
  disabled = false, // Accept a disabled prop, default to false
  currentJobType = null // Current job type to check paper availability
}) => {
  // State for modal visibility
  const [isModalOpen, setIsModalOpen] = useState(false);
  // State for search and filtering 
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  // State for the selected paper category (for the first dropdown)
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Get the currently selected paper object
  const selectedPaper = useMemo(() => 
    paperStocks.find(p => p.id === selectedPaperId),
    [selectedPaperId, paperStocks]
  );
  
  // Check if selected paper is a common paper
  const isSelectedCommon = useMemo(() => {
    return selectedPaper?.display_tier === "common";
  }, [selectedPaper]);
  
  // Group common papers by category
  const commonPapersByCategory = useMemo(() => {
    // Filter papers marked as "common" in display_tier
    const commonPapers = paperStocks.filter(paper => 
      paper.display_tier === "common"
    );
    
    // Group by category (Gloss, Silk, Uncoated)
    return commonPapers.reduce((acc, paper) => {
      const category = paper.category || "Other";
      if (!acc[category]) acc[category] = [];
      acc[category].push(paper);
      return acc;
    }, {});
  }, [paperStocks]);
  
  // Get unique categories from common papers
  const availableCategories = useMemo(() => {
    return Object.keys(commonPapersByCategory).sort();
  }, [commonPapersByCategory]);
  
  // Get weights for the selected category
  const weightsForSelectedCategory = useMemo(() => {
    if (!selectedCategory || !commonPapersByCategory[selectedCategory]) {
      return [];
    }
    
    // Get papers for this category and sort by weight
    return commonPapersByCategory[selectedCategory]
      .sort((a, b) => a.weight_gsm - b.weight_gsm);
  }, [selectedCategory, commonPapersByCategory]);
  
  // Get unique categories from all papers (for the modal)
  const allCategories = useMemo(() => {
    const uniqueCategories = new Set(
      paperStocks
        .map(p => p.category)
        .filter(Boolean) // Remove empty values
    );
    return ['all', ...Array.from(uniqueCategories).sort()];
  }, [paperStocks]);
  
  // Filter papers for the modal
  const filteredPapers = useMemo(() => {
    return paperStocks.filter(paper => {
      // Apply category filter
      if (categoryFilter !== 'all' && paper.category !== categoryFilter) {
        return false;
      }
      
      // Apply search filter
      if (searchQuery) {
        const normalizedQuery = searchQuery.toLowerCase();
        return (
          paper.name.toLowerCase().includes(normalizedQuery) ||
          paper.id.toLowerCase().includes(normalizedQuery) ||
          String(paper.weight_gsm).includes(normalizedQuery)
        );
      }
      
      return true;
    });
  }, [paperStocks, searchQuery, categoryFilter]);
  
  // Group filtered papers by category for the modal
  const groupedFilteredPapers = useMemo(() => {
    return filteredPapers.reduce((acc, paper) => {
      const category = paper.category || "Other";
      if (!acc[category]) acc[category] = [];
      acc[category].push(paper);
      return acc;
    }, {});
  }, [filteredPapers]);

  // Sort categories alphabetically
  const sortedCategories = useMemo(() => {
    return Object.keys(groupedFilteredPapers).sort();
  }, [groupedFilteredPapers]);

  // Group papers by name first, then by weight within name groups
  const groupPapersByName = (papers) => {
    // First, group papers by name (ignoring weight)
    const nameGroups = papers.reduce((acc, paper) => {
      // Extract the base name without the weight information
      const baseName = paper.name.replace(/\s*\d+gsm\s*|\s*\(\d+gsm\)\s*/gi, '').trim();
      
      if (!acc[baseName]) {
        acc[baseName] = [];
      }
      acc[baseName].push(paper);
      return acc;
    }, {});
    
    // For each name group, sort by weight
    Object.keys(nameGroups).forEach(name => {
      nameGroups[name].sort((a, b) => a.weight_gsm - b.weight_gsm);
    });
    
    // Return as a flat array, with name groups in alphabetical order
    return Object.keys(nameGroups)
      .sort() // Sort names alphabetically
      .reduce((acc, name) => {
        return [...acc, ...nameGroups[name]];
      }, []);
  };

  // Sort papers with "common" at the top while preserving name/weight grouping
  const sortPapersWithCommonFirst = (papers) => {
    // Split into common and non-common papers
    const common = papers.filter(p => p.display_tier === "common");
    const nonCommon = papers.filter(p => p.display_tier !== "common");
    
    // Apply groupPapersByName to each set
    const groupedCommon = groupPapersByName(common);
    const groupedNonCommon = groupPapersByName(nonCommon);
    
    // Combine with common papers first
    return [...groupedCommon, ...groupedNonCommon];
  };

  // Update selected category based on the selected paper
  useEffect(() => {
    if (selectedPaper?.display_tier === "common" && selectedPaper?.category) {
      setSelectedCategory(selectedPaper.category);
    }
  }, [selectedPaper]);

  // Handle category selection
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    // Don't change the selected paper when only the category changes
  };

  // Handle weight selection
  const handleWeightChange = (e) => {
    onChange(e.target.value); // This changes the actual selected paper
  };

  // Check if a paper is available for the current job type
  const isPaperAvailable = (paper) => {
    if (!currentJobType || !paper.allowed_job_types) {
      // If no job type selected or no restrictions on paper, it's available
      return true;
    }

    // If paper has allowed_job_types restriction, check if current job type is in the list
    const allowedTypes = Array.isArray(paper.allowed_job_types)
      ? paper.allowed_job_types
      : paper.allowed_job_types;

    if (Array.isArray(allowedTypes) && allowedTypes.length > 0) {
      return allowedTypes.includes(currentJobType);
    }

    return true;
  };

  return (
    <div className={className}>
      {/* Two-step selection with side-by-side dropdowns */}
      <div className="flex space-x-2 mb-3">
        {/* Step 1: Paper Type dropdown */}
        <div className="w-1/2">
          <select
            className="w-full p-2 border rounded"
            value={selectedCategory}
            onChange={handleCategoryChange}
            disabled={disabled} // Apply the disabled prop
          >
            <option value="">Select Paper Type</option>
            {availableCategories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
        
        {/* Step 2: Weight dropdown */}
        <div className="w-1/2">
          <select
            className="w-full p-2 border rounded"
            value={isSelectedCommon && selectedPaper?.category === selectedCategory ? selectedPaperId : ""}
            onChange={handleWeightChange}
            disabled={disabled || !selectedCategory} // Disable if globally disabled OR no category selected
          >
            <option value="">Select Weight</option>
            {weightsForSelectedCategory.map(paper => (
              <option
                key={paper.id}
                value={paper.id}
                disabled={!isPaperAvailable(paper)}
              >
                {paper.weight_gsm}gsm
                {!isPaperAvailable(paper) ? ' (unavailable)' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Display selected paper if it's not in common papers */}
      {selectedPaperId && !isSelectedCommon && (
        <div className="bg-blue-50 p-2 mb-2 rounded border border-blue-200 flex justify-between items-center">
          <span>
            {selectedPaper?.name} ({selectedPaper?.weight_gsm}gsm)
          </span>
          <button 
            type="button"
            className="text-xs text-red-600"
            onClick={() => onChange("")}
          >
            ×
          </button>
        </div>
      )}
      
      {/* More Papers Button */}
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="w-full py-2 px-3 mt-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={disabled} // Apply the disabled prop
      >
        <span>{selectedPaperId && !isSelectedCommon ? 'Change Paper' : 'More Papers'}</span>
        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      
      {/* Paper Modal using Headless UI */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog 
          as="div" 
          className="relative z-10" 
          onClose={() => setIsModalOpen(false)}
        >
          {/* Background overlay */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-fit max-w-[90vw] min-w-80 sm:min-w-96 transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Select Paper Stock
                  </Dialog.Title>
                  
                  {/* Search and filter controls */}
                  <div className="mt-4 mb-4 space-y-2">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search paper stocks..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full p-2 pl-8 border rounded"
                      />
                      <svg className="w-4 h-4 absolute left-2 top-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    </div>
                    
                    <div className="flex flex-nowrap gap-2 overflow-x-auto sm:overflow-x-visible pb-1">
                      {allCategories.map(category => (
                        <button
                          key={category}
                          type="button"
                          className={`px-3 py-1 text-sm rounded whitespace-nowrap ${
                            category === categoryFilter
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                          onClick={() => setCategoryFilter(category)}
                        >
                          {category === 'all' ? 'All Types' : category}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Paper list organized by category */}
                  <div className="max-h-72 overflow-y-auto">
                    {sortedCategories.length > 0 ? (
                      sortedCategories.map((category) => (
                        <div key={category} className="mb-4">
                          <h4 className="text-sm font-semibold mb-1 text-gray-700">{category}</h4>
                          <div className="space-y-1">
                            {/* Group papers by common first, then by name and weight */}
                            {sortPapersWithCommonFirst(groupedFilteredPapers[category]).map(paper => {
                              const isAvailable = isPaperAvailable(paper);
                              return (
                                <div
                                  key={paper.id}
                                  title={!isAvailable ? 'This paper is unavailable for this job type' : ''}
                                  className="relative"
                                >
                                  <button
                                    type="button"
                                    disabled={!isAvailable}
                                    className={`w-full text-left p-2 rounded flex items-center ${
                                      paper.id === selectedPaperId && isAvailable
                                        ? 'bg-blue-100 border border-blue-300'
                                        : isAvailable
                                        ? 'hover:bg-gray-100'
                                        : 'bg-gray-100 text-gray-400 opacity-50 cursor-not-allowed'
                                    }`}
                                    onClick={() => {
                                      if (isAvailable) {
                                        onChange(paper.id);
                                        setIsModalOpen(false);
                                      }
                                    }}
                                  >
                                    <span className="flex-grow">
                                      {paper.name}
                                      {paper.description && (
                                        <span className={`${isAvailable ? 'text-gray-600 opacity-70' : 'text-gray-400 opacity-50'}`}> — {paper.description}</span>
                                      )}
                                    </span>
                                    {paper.display_tier === "common" && isAvailable && (
                                      <span className="text-xs text-blue-600 ml-1">
                                        ★
                                      </span>
                                    )}
                                  </button>
                                  {!isAvailable && (
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center bg-red-500 rounded-full text-white text-xs font-bold" title="Unavailable for this job type">
                                      ✕
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No papers match your search.
                      </div>
                    )}
                  </div>
                  
                  {/* Modal actions */}
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => { onChange(""); setIsModalOpen(false); }}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded"
                      disabled={!selectedPaperId}
                    >
                      Clear Selection
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default PaperSelector;